import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { EmptyState, StatusBadge, TWButton, TWDataTable } from '../../../components/ui';
import { DataTableSkeleton, colTitle } from '../../../data/columns';
import fleetApi from '../../../services/fleetApi';
import useFleetPoll, { fleetErrorText } from '../use-fleet-poll';
import {
  COMMAND_TYPES,
  PAGE_SIZE,
  isHttpUrl,
  isTerminalCommand,
  relativeSeen,
  statusLabel,
  statusTone,
} from '../fleet-utils';
import { absoluteTime, pageOf, truncate, writePage } from './device-tab-utils';

const COMMANDS_POLL_MS = 10000;
const ARGS_MAX = 40;
const RESULT_MAX = 60;
const DASH = '—';

const SKELETON_HEADERS = [
  { label: 'When', w: 'w-24' },
  { label: 'Who', w: 'w-36' },
  { label: 'Type', w: 'w-24' },
  { label: 'Args', w: 'w-32' },
  { label: 'Status', shape: 'pill', w: 'w-20' },
  { label: 'Duration', align: 'right', w: 'w-10' },
  { label: 'Result', w: 'w-28' },
  { label: '', w: 'w-12' },
];

const typeLabel = (type) => COMMAND_TYPES.find((c) => c.type === type)?.label || type;

const durationText = (cmd) => {
  if (!cmd.finishedat || !cmd.createdate) return DASH;
  const seconds = Math.round((Date.parse(cmd.finishedat) - Date.parse(cmd.createdate)) / 1000);
  return Number.isFinite(seconds) ? Math.max(0, seconds) + ' s' : DASH;
};

// Security carry D1: ONLY the top-level result.resulturl is ever a link, and
// only when it is an http(s) URL. installedversioncode is text; every other
// value of result (nested objects included) is JSON text, never a URL, HTML
// or href.
const ResultCell = ({ cmd }) => {
  const result = cmd.result;
  const isObject = result !== null && typeof result === 'object' && !Array.isArray(result);
  const { resulturl, installedversioncode, ...rest } = isObject ? result : {};
  const extra = isObject ? rest : result;
  const hasExtra =
    extra !== null &&
    extra !== undefined &&
    (isObject ? Object.keys(extra).length > 0 : true);
  const extraText = hasExtra ? JSON.stringify(extra) : '';
  const linked = isHttpUrl(resulturl);
  const looseUrl = isObject && resulturl !== undefined && resulturl !== null && !linked;
  const shown = [];
  if (linked) {
    shown.push(
      <a
        key="url"
        href={resulturl}
        target="_blank"
        rel="noreferrer"
        className="text-sm font-medium text-blue-600 hover:underline dark:text-sky-400"
      >
        Open
      </a>,
    );
  }
  if (looseUrl) {
    shown.push(
      <span key="loose" className="font-mono text-xs text-slate-600 dark:text-slate-300">
        {truncate(JSON.stringify({ resulturl }), RESULT_MAX)}
      </span>,
    );
  }
  if (installedversioncode !== undefined && installedversioncode !== null) {
    shown.push(
      <span key="ver" className="text-sm text-slate-700 dark:text-slate-200">
        {String(installedversioncode)}
      </span>,
    );
  }
  if (extraText) {
    shown.push(
      <span
        key="extra"
        title={extraText}
        className="font-mono text-xs text-slate-600 dark:text-slate-300"
      >
        {truncate(extraText, RESULT_MAX)}
      </span>,
    );
  }
  if (cmd.error) {
    shown.push(
      <span key="error" className="text-sm text-rose-600 dark:text-rose-400">
        {cmd.error}
      </span>,
    );
  }
  if (shown.length === 0) return DASH;
  return <div className="flex flex-col items-start gap-0.5">{shown}</div>;
};

const SKELETON_ROWS = 8;

// The commands of this device, newest first. Refreshes every 10 s while any
// row is still in flight and stops once all are terminal.
// `commandsVersion` is bumped by the header after a command it sent, so the
// list refetches without a tab switch.
const DeviceCommandsTab = ({ device, commandsVersion }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = pageOf(searchParams);
  const query = `?pageNumber=${page}&pageSize=${PAGE_SIZE}`;
  const [active, setActive] = useState(false);
  const [cancelling, setCancelling] = useState(null);

  // `poll` gates only the interval: the first load and refetches are not
  // affected and flipping it costs no request.
  const { data, loading, fetching, error, stalled, reload } = useFleetPoll(
    () => fleetApi.listDeviceCommands(device.id, query).then((res) => res.data),
    {
      intervalMs: COMMANDS_POLL_MS,
      poll: active,
      deps: [device.id, query, commandsVersion],
      onRefetchError: (err) => message.error(fleetErrorText(err, 'Could not load commands')),
    },
  );

  const list = data?.data || [];
  const totalRecords = data?.totalRecords ?? 0;

  useEffect(() => {
    setActive((data?.data || []).some((cmd) => !isTerminalCommand(cmd.status)));
  }, [data]);

  // A page past the end answers [] with a positive count: go back to page 1.
  const pastTheEnd = !loading && !error && list.length === 0 && totalRecords > 0 && page > 1;
  useEffect(() => {
    if (pastTheEnd) writePage(setSearchParams, 1);
  }, [pastTheEnd, setSearchParams]);

  const cancel = async (cmd) => {
    setCancelling(cmd.id);
    try {
      await fleetApi.cancelCommand(cmd.id);
      message.success('Command cancelled');
      reload();
    } catch (err) {
      message.error(fleetErrorText(err, 'Something went wrong. Try again.'));
    } finally {
      setCancelling(null);
    }
  };

  const columns = [
    {
      title: colTitle('When'),
      key: 'when',
      render: (_, cmd) => (
        <span
          title={absoluteTime(cmd.createdate)}
          className="whitespace-nowrap text-sm text-slate-600 dark:text-slate-300"
        >
          {relativeSeen(cmd.createdate)}
        </span>
      ),
    },
    {
      title: colTitle('Who'),
      key: 'who',
      render: (_, cmd) => (
        <span className="text-sm text-slate-600 dark:text-slate-300">
          {cmd.createdbyemail || 'system'}
        </span>
      ),
    },
    {
      title: colTitle('Type'),
      key: 'type',
      render: (_, cmd) => (
        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
          {typeLabel(cmd.type)}
        </span>
      ),
    },
    {
      title: colTitle('Args'),
      key: 'args',
      render: (_, cmd) => {
        const full = JSON.stringify(cmd.args ?? {});
        return (
          <span title={full} className="font-mono text-xs text-slate-600 dark:text-slate-300">
            {truncate(full, ARGS_MAX)}
          </span>
        );
      },
    },
    {
      title: colTitle('Status'),
      key: 'status',
      render: (_, cmd) => (
        <StatusBadge size="sm" tone={statusTone(cmd.status)} label={statusLabel(cmd.status)} />
      ),
    },
    {
      title: colTitle('Duration', { align: 'right' }),
      key: 'duration',
      align: 'right',
      render: (_, cmd) => <span className="tabular-nums">{durationText(cmd)}</span>,
    },
    {
      title: colTitle('Result'),
      key: 'result',
      render: (_, cmd) => <ResultCell cmd={cmd} />,
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      render: (_, cmd) =>
        cmd.status === 'queued' ? (
          <TWButton
            variant="ghost"
            size="sm"
            loading={cancelling === cmd.id}
            onClick={() => cancel(cmd)}
          >
            Cancel
          </TWButton>
        ) : null,
    },
  ];

  if (error) {
    return (
      <EmptyState
        icon={ExclamationTriangleIcon}
        title="Could not load commands"
        description={fleetErrorText(error)}
        action={<TWButton onClick={reload}>Retry</TWButton>}
      />
    );
  }
  if (loading || pastTheEnd) {
    return <DataTableSkeleton headers={SKELETON_HEADERS} rows={SKELETON_ROWS} />;
  }
  if (totalRecords === 0) {
    return (
      <EmptyState
        title="No commands yet"
        description="Commands sent to this device show up here."
      />
    );
  }
  return (
    <div>
      {stalled ? (
        <div
          role="status"
          className="mb-3 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
        >
          <span>Live updates paused.</span>
          <TWButton variant="link" size="sm" onClick={reload}>
            Retry
          </TWButton>
        </div>
      ) : null}
      <TWDataTable
        columns={columns}
        data={list}
        loading={fetching}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: totalRecords,
          showSizeChanger: false,
          showTotal: (t) => `${t} ${t === 1 ? 'command' : 'commands'}`,
          onChange: (next) => writePage(setSearchParams, next),
        }}
      />
    </div>
  );
};

export default DeviceCommandsTab;
